import { useQuery } from '@tanstack/react-query';
import { PassportSectionFallback } from './PassportSectionFallback';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { Award } from 'lucide-react';
import { format } from 'date-fns';

interface PassportRoleTracksProps {
  technicianProfileId: string;
  /** When provided, skip the technician_users lookup and use this user_id directly. */
  optionalUserId?: string;
}

/**
 * Passport Role Track sub-sections.
 * Queries membership_role_events for distinct roles and shows placeholder track sections.
 */
export function PassportRoleTracks({ technicianProfileId, optionalUserId }: PassportRoleTracksProps) {
  // Resolve user_id: use optionalUserId directly (passport mode) or walk the lookup chain
  const { data: resolvedUserId, isError: userLookupFailed } = useQuery({
    queryKey: ['tech-profile-user', technicianProfileId],
    queryFn: async () => {
      // CORE-C GAP-003: discarding these errors made a failed lookup look
      // exactly like a person with no technician link — the section then
      // vanished, which is the answer reserved for "not applicable".
      const { data, error } = await supabase
        .from('technicians')
        .select('id')
        .eq('technician_profile_id', technicianProfileId)
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      if (!data?.id) return null;

      const { data: tu, error: tuError } = await supabase
        .from('technician_users')
        .select('user_id')
        .eq('technician_id', data.id)
        .limit(1)
        .maybeSingle();

      if (tuError) throw tuError;
      return tu?.user_id || null;
    },
    enabled: !!technicianProfileId && !optionalUserId,
  });

  const userId = optionalUserId || resolvedUserId;

  // Query role events for distinct roles
  const { data: roleEvents, isError: roleEventsFailed } = useQuery({
    queryKey: ['role-tracks', userId],
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from('membership_role_events')
        .select('to_role, changed_at')
        .eq('user_id', userId)
        .order('changed_at', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!userId,
  });

  // ── Order matters: APPLICABILITY, then retrieval state ──────────────────
  //
  // The TIER gate lives outside this component: PassportHome wraps it in
  // `RequireFeature feature="passportAdvancedIntelligence"`, so a viewer
  // without the entitlement never mounts it and no query of its runs. This
  // check is the applicability half — a Passport with no professional handle
  // at all has no role history to speak of, decided without any read.
  if (!technicianProfileId && !optionalUserId) return null;

  // Entitled and applicable. Either read failing is now visible as a failure
  // rather than as a person who has only ever held one role.
  if (userLookupFailed || roleEventsFailed) {
    return <PassportSectionFallback sectionName="Role Tracks" />;
  }

  // Genuine absence keeps its existing behaviour: the section disappears.
  if (!roleEvents || roleEvents.length === 0) return null;

  // Get distinct roles with first activation date
  const roleTracks = new Map<string, string>();
  for (const event of roleEvents) {
    if (!roleTracks.has(event.to_role)) {
      roleTracks.set(event.to_role, event.changed_at);
    }
  }

  // Only show if there are multiple distinct roles
  if (roleTracks.size < 2) return null;

  const trackLabels: Record<string, string> = {
    TECHNICIAN: 'Technician Track',
    SERVICE_WRITER: 'Advisor Track',
    MANAGER: 'Manager Track',
    OWNER: 'Owner Track',
    DISPATCHER: 'Dispatcher Track',
    PARTS: 'Parts Track',
    AUDITOR: 'Auditor Track',
    ORG_ADMIN: 'Org Admin Track',
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Award className="h-5 w-5 text-primary" />
          Role Tracks
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {Array.from(roleTracks.entries()).map(([role, activatedAt]) => (
          <div
            key={role}
            className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
          >
            <div className="flex items-center gap-3">
              <Badge variant="secondary">{role}</Badge>
              <span className="text-sm font-medium">
                {trackLabels[role] || `${role} Track`}
              </span>
            </div>
            <span className="text-xs text-muted-foreground">
              Activated {format(new Date(activatedAt), 'MMM d, yyyy')}
            </span>
          </div>
        ))}
        <p className="text-xs text-muted-foreground pt-1">
          Training data displays globally across all tracks.
        </p>
      </CardContent>
    </Card>
  );
}
